import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, Send, X, RefreshCw, CheckCircle, AlertCircle,
  Loader2, MessageSquare, ChevronDown, ChevronUp,
} from 'lucide-react';
import API from '../api/axios';
import { useWhatsApp } from '../context/WhatsAppContext';

const WhatsAppGroup = () => {
  const { status } = useWhatsApp();
  const isConnected = status === 'connected';

  // ── Bulk send state ──────────────────────────────────────────────────────
  const [numbers, setNumbers] = useState([]);
  const [inputNum, setInputNum] = useState('');
  const [bulkMessage, setBulkMessage] = useState('');
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkResults, setBulkResults] = useState([]);

  // ── Group messaging state ────────────────────────────────────────────────
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupMessage, setGroupMessage] = useState('');
  const [groupSending, setGroupSending] = useState(false);
  const [groupResult, setGroupResult] = useState(null);
  const [expandedGroup, setExpandedGroup] = useState(null);

  const fetchGroups = useCallback(async () => {
    if (!isConnected) return;
    setGroupsLoading(true);
    try {
      const res = await API.get('/groups');
      setGroups(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Fetch groups error:', err);
    } finally {
      setGroupsLoading(false);
    }
  }, [isConnected]);

  useEffect(() => {
    if (isConnected) fetchGroups();
  }, [isConnected, fetchGroups]);

  const addNumber = () => {
    const clean = inputNum.replace(/\D/g, '');
    if (clean && !numbers.includes(clean)) {
      setNumbers(prev => [...prev, clean]);
      setInputNum('');
    }
  };

  const removeNumber = (num) => setNumbers(prev => prev.filter(n => n !== num));

  const handleBulkSend = async () => {
    if (numbers.length === 0 || !bulkMessage.trim()) return;
    setBulkSending(true);
    setBulkResults([]);
    try {
      const res = await API.post('/send-bulk', { numbers, message: bulkMessage });
      setBulkResults(res.data.results || []);
    } catch (err) {
      setBulkResults([{ number: 'all', success: false, error: err.response?.data?.error || err.message }]);
    } finally {
      setBulkSending(false);
    }
  };

  const handleGroupSend = async () => {
    if (!selectedGroup || !groupMessage.trim()) return;
    setGroupSending(true);
    setGroupResult(null);
    try {
      const res = await API.post('/send-group-message', {
        groupJid: selectedGroup.jid,
        message: groupMessage,
      });
      setGroupResult({ success: true, messageId: res.data.messageId });
      setGroupMessage('');
    } catch (err) {
      setGroupResult({ success: false, error: err.response?.data?.error || err.message });
    } finally {
      setGroupSending(false);
    }
  };

  const successCount = bulkResults.filter(r => r.success).length;
  const failCount = bulkResults.filter(r => !r.success).length;

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8">
      <div className="max-w-4xl mx-auto space-y-8">

        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-500 flex items-center justify-center">
              <Users size={20} className="text-white" />
            </div>
            Groups & Bulk Messaging
          </h1>
          <p className="mt-2 text-slate-500">Send messages to WhatsApp groups or multiple contacts at once.</p>
        </div>

        {/* Connection warning */}
        {!isConnected && (
          <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-700">
            <AlertCircle size={20} className="flex-shrink-0" />
            <p className="text-sm font-medium">
              WhatsApp is not connected. Please{' '}
              <a href="/generate-qr" className="underline font-semibold">link your device</a> first.
            </p>
          </div>
        )}

        <div className="grid gap-8 lg:grid-cols-2">

          {/* ── Bulk Send Panel ─────────────────────────────────────────── */}
          <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6">
            <h2 className="text-lg font-bold text-slate-800 mb-1 flex items-center gap-2">
              <Send size={18} className="text-green-500" />
              Bulk Send
            </h2>
            <p className="text-sm text-slate-500 mb-5">Send the same message to multiple phone numbers.</p>

            {/* Number input */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Add Numbers</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Phone number (with country code)"
                  value={inputNum}
                  onChange={e => setInputNum(e.target.value.replace(/\D/g, ''))}
                  onKeyDown={e => e.key === 'Enter' && addNumber()}
                  className="flex-1 px-3 py-2.5 border border-gray-300 rounded-xl text-sm outline-none focus:ring-2 focus:ring-green-300 transition"
                />
                <button
                  onClick={addNumber}
                  disabled={!inputNum.trim()}
                  className="px-4 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-xl text-sm font-medium transition disabled:opacity-40"
                >
                  Add
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-400">Press Enter or click Add. Include country code (e.g. 91…)</p>
            </div>

            {/* Number tags */}
            {numbers.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4 p-3 bg-gray-50 rounded-xl border border-gray-200">
                {numbers.map(num => (
                  <span
                    key={num}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white border border-gray-200 text-gray-700 rounded-full text-sm shadow-sm"
                  >
                    {num}
                    <button
                      onClick={() => removeNumber(num)}
                      className="text-gray-400 hover:text-red-500 transition"
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
                <button
                  onClick={() => setNumbers([])}
                  className="text-xs text-red-400 hover:text-red-600 ml-auto"
                >
                  Clear all
                </button>
              </div>
            )}

            {/* Message */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Message</label>
              <textarea
                rows={4}
                placeholder="Type the message to send to all numbers…"
                value={bulkMessage}
                onChange={e => setBulkMessage(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm outline-none focus:ring-2 focus:ring-green-300 transition resize-none"
              />
            </div>

            {/* Results summary */}
            {bulkResults.length > 0 && (
              <div className="mb-4 p-3 bg-gray-50 rounded-xl border border-gray-200 space-y-1.5 max-h-40 overflow-y-auto">
                <div className="flex gap-4 text-sm font-semibold mb-2">
                  <span className="text-green-600">✓ {successCount} sent</span>
                  {failCount > 0 && <span className="text-red-500">✗ {failCount} failed</span>}
                </div>
                {bulkResults.map((r, i) => (
                  <div key={i} className={`flex items-center gap-2 text-xs ${r.success ? 'text-green-700' : 'text-red-600'}`}>
                    {r.success
                      ? <CheckCircle size={12} />
                      : <AlertCircle size={12} />
                    }
                    <span className="font-mono">{r.number}</span>
                    {!r.success && <span className="text-gray-400">— {r.error}</span>}
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={handleBulkSend}
              disabled={bulkSending || numbers.length === 0 || !bulkMessage.trim() || !isConnected}
              className="w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold text-sm transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {bulkSending ? (
                <><Loader2 size={16} className="animate-spin" /> Sending…</>
              ) : (
                <><Send size={16} /> Send to {numbers.length} number{numbers.length !== 1 ? 's' : ''}</>
              )}
            </button>
          </div>

          {/* ── Group Messaging Panel ────────────────────────────────────── */}
          <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <MessageSquare size={18} className="text-indigo-500" />
                Group Message
              </h2>
              <button
                onClick={fetchGroups}
                disabled={groupsLoading || !isConnected}
                className="p-1.5 text-gray-400 hover:text-indigo-500 hover:bg-indigo-50 rounded-lg transition disabled:opacity-40"
                title="Refresh groups"
              >
                <RefreshCw size={16} className={groupsLoading ? 'animate-spin' : ''} />
              </button>
            </div>
            <p className="text-sm text-slate-500 mb-5">Select a group and send a message to all members.</p>

            {/* Groups list */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                Select Group {groups.length > 0 && <span className="text-gray-400 font-normal">({groups.length} found)</span>}
              </label>

              {groupsLoading ? (
                <div className="flex items-center justify-center py-8 text-gray-400">
                  <Loader2 size={24} className="animate-spin mr-2" />
                  <span className="text-sm">Loading groups…</span>
                </div>
              ) : groups.length === 0 ? (
                <div className="py-8 text-center text-gray-400">
                  <Users size={32} className="mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No groups found.</p>
                  <p className="text-xs mt-1">Make sure WhatsApp is connected and you're in some groups.</p>
                  <button
                    onClick={fetchGroups}
                    disabled={!isConnected}
                    className="mt-3 text-xs text-indigo-500 hover:text-indigo-700 underline"
                  >
                    Refresh
                  </button>
                </div>
              ) : (
                <div className="space-y-1.5 max-h-52 overflow-y-auto pr-1">
                  {groups.map(g => (
                    <div key={g.jid}>
                      <button
                        onClick={() => {
                          setSelectedGroup(g);
                          setGroupResult(null);
                          setExpandedGroup(expandedGroup === g.jid ? null : g.jid);
                        }}
                        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm transition border ${
                          selectedGroup?.jid === g.jid
                            ? 'bg-indigo-50 border-indigo-300 text-indigo-700'
                            : 'bg-gray-50 border-gray-200 text-gray-700 hover:border-indigo-200 hover:bg-indigo-50/50'
                        }`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                            <Users size={14} className="text-indigo-500" />
                          </div>
                          <div className="text-left min-w-0">
                            <p className="font-medium truncate">{g.name}</p>
                            <p className="text-xs text-gray-400">{g.participants} members</p>
                          </div>
                        </div>
                        {expandedGroup === g.jid
                          ? <ChevronUp size={14} className="flex-shrink-0" />
                          : <ChevronDown size={14} className="flex-shrink-0" />
                        }
                      </button>
                      {expandedGroup === g.jid && g.description && (
                        <div className="px-3 py-2 text-xs text-gray-500 bg-gray-50 rounded-b-xl border border-t-0 border-gray-200">
                          {g.description}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Group message input */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Message</label>
              <textarea
                rows={4}
                placeholder={selectedGroup ? `Message to ${selectedGroup.name}…` : 'Select a group first…'}
                value={groupMessage}
                onChange={e => setGroupMessage(e.target.value)}
                disabled={!selectedGroup}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm outline-none focus:ring-2 focus:ring-indigo-300 transition resize-none disabled:bg-gray-50 disabled:text-gray-400"
              />
            </div>

            {/* Group result */}
            {groupResult && (
              <div className={`mb-4 flex items-start gap-2 p-3 rounded-xl border text-sm ${
                groupResult.success
                  ? 'bg-green-50 border-green-200 text-green-700'
                  : 'bg-red-50 border-red-200 text-red-700'
              }`}>
                {groupResult.success
                  ? <CheckCircle size={16} className="flex-shrink-0 mt-0.5" />
                  : <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                }
                <span>
                  {groupResult.success
                    ? `Message sent to ${selectedGroup?.name}!`
                    : groupResult.error
                  }
                </span>
              </div>
            )}

            <button
              onClick={handleGroupSend}
              disabled={groupSending || !selectedGroup || !groupMessage.trim() || !isConnected}
              className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold text-sm transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {groupSending ? (
                <><Loader2 size={16} className="animate-spin" /> Sending…</>
              ) : (
                <><Send size={16} /> Send to Group</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppGroup;
